import { TranslateService } from '@ngx-translate/core';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { UntypedFormBuilder, Validators, ValidationErrors, UntypedFormControl } from '@angular/forms';
import { Observable } from 'rxjs';
import { InsurerValidators } from 'src/app/core/validators/insurer.validators';
import { InsurerEdit } from '../model/insurerEdit';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { Dictionary } from 'src/app/core/model/dictionary';
import { DictionaryService } from 'src/app/core/service/dictionary.service';
import { SettingService } from 'src/app/core/service/setting.service';

@Component({
  selector: 'app-insurer-edit',
  templateUrl: './insurer-edit.component.html',
  styleUrls: ['./insurer-edit.component.scss']
})
export class InsurerEditComponent
{
  @Input() public isNew = false;
  @Input() public revenueManager = false;

  @Output() public cancelling: EventEmitter<any> = new EventEmitter();
  @Output() public save: EventEmitter<InsurerEdit> = new EventEmitter();

  public editForm: any;
  public editFormActive = false;
  public insurersLookup: any = [];
  public addTitle = '';
  public editTitle = '';
  public tabName = '';
  public nameAlreadyExists = '';
  public feeSchedulesFiltered: Dictionary[] = [];
  public allFeeSchedules = [];

  /**
   * Current search term entered from a comboxbox
   */
  public searchTerm: string;

  public urlPattern = /^(?:https?:\/\/)?[\w.-]+(?:\.[\w.-]+)+[\w\-._~:/?#[\]@!$&'()*+,;=.]+$/;

  /**
   * Phone number input format mask
   */
  public phoneNumberMask: string;

  /**
   * Maximum number of items for virtualization. This is to accelerate the DOM performance.
   */
  public virtualFeeSchedule: any = { itemHeight: 30 };

  private insurerEdit: InsurerEdit;

  constructor(
    private fb: UntypedFormBuilder,
    private translate: TranslateService,
    private insurerValidators: InsurerValidators,
    private messageHandlerService: MessageHandlerService,
    private dictionaryService: DictionaryService,
    private settingService: SettingService,
  )
  {
  }

  public get name(): UntypedFormControl
  {
    return this.editForm.get('name');
  }

  public get contactName(): UntypedFormControl
  {
    return this.editForm.get('contactName');
  }

  public get contactPhone(): UntypedFormControl
  {
    return this.editForm.get('contactPhone');
  }

  public get contactEmail(): UntypedFormControl
  {
    return this.editForm.get('contactEmail');
  }

  public get url(): UntypedFormControl
  {
    return this.editForm.get('url');
  }

  public get invoiceNumberSuffix(): UntypedFormControl
  {
    return this.editForm.get('invoiceNumberSuffix');
  }

  public get feeSchedules(): UntypedFormControl
  {
    return this.editForm.get('feeSchedules');
  }

  public get active(): UntypedFormControl
  {
    return this.editForm.get('enaactivebled');
  }

  @Input() public set model(insurer: InsurerEdit)
  {
    this.initModel(insurer);
  }

  public onSave(e: Event): void
  {
    e.preventDefault();
    const objectToPersist = this.editForm.value;

    this.save.emit(objectToPersist);
    this.editFormActive = false;
  }

  public onCancel(e: Event): void
  {
    e.preventDefault();
    this.closeEditForm();
  }

  public isValidUrl(): boolean
  {
    const regEx = new RegExp(this.urlPattern);
    return this.url.value !== undefined && this.url.value && regEx.test(this.url.value);
  }

  public openUrl(): void
  {
    if (this.isValidUrl())
    {
      window.open(this.url.value);
    }
  }

  /**
   * Filters the Fee SChedules based on user input.
   */
  public filterFeeSchedules(filter: string): void
  {
    this.searchTerm = filter;
    this.feeSchedulesFiltered = this.allFeeSchedules
      .filter(user => user.displayName.toLowerCase().includes(filter.toLowerCase()));
  }

  private closeEditForm(): void
  {
    this.editFormActive = false;
    this.cancelling.emit();
  }

  private initModel(insurerEdit: InsurerEdit)
  {
    if (insurerEdit === undefined)
    {
      return;
    }

    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.phoneNumberMask = sessionSetting.phoneMask;
      }
    });

    this.translate.get(['INSURER', 'ADD-INSURER', 'EDIT-INSURER', 'INSURER-ALREADY-EXISTS',
      'PAYER-NAME', 'ADD-PAYER', 'EDIT-PAYER', 'PAYER-ALREADY-EXISTS',]).subscribe({
        next: (res) =>
        {
          if (this.revenueManager)
          {
            this.tabName = res['PAYER-NAME'];
            this.addTitle = res['ADD-PAYER'];
            this.editTitle = res['EDIT-PAYER'];
            this.nameAlreadyExists = res['PAYER-ALREADY-EXISTS'];
          }
          else
          {
            this.tabName = res.INSURER;
            this.addTitle = res['ADD-INSURER'];
            this.editTitle = res['EDIT-INSURER'];
            this.nameAlreadyExists = res['INSURER-ALREADY-EXISTS'];
          }
        }
      });

    this.dictionaryService.getCachedFeeScheduless().subscribe({
      next: (data) =>
      {
        this.allFeeSchedules = data;
        this.filterFeeSchedules('');
      }
    });

    // Set the current insurer id used for name async validators
    const previousInsurerName = this.isNew ? '' : insurerEdit.name;

    // Set form
    this.editForm = this.fb.group({
      id: [insurerEdit.id],
      name: [
        { value: '', disabled: false },
        {
          validators: [Validators.required],
          asyncValidators: (): Observable<ValidationErrors | null> =>
            this.insurerValidators.validateInsurerNameUnique(this.editForm.get('name'), previousInsurerName)
        }
      ],
      contactName: { value: insurerEdit.contactName, disabled: false },
      contactPhone: { value: insurerEdit.contactPhone, disabled: false },
      contactEmail: [{ value: insurerEdit.contactEmail, disabled: false }, Validators.email],
      url: [{ value: insurerEdit.url, disabled: false }, Validators.pattern(this.urlPattern)],
      invoiceNumberSuffix: [{ value: insurerEdit.invoiceNumberSuffix, disabled: false }, this.revenueManager ? Validators.required : false],
      feeSchedules: [{
        value: this.insurerEdit !== undefined &&
          this.insurerEdit.feeSchedules !== null &&
          this.insurerEdit.feeSchedules !== undefined ?
          this.insurerEdit.feeSchedules : null, disabled: false
      }, this.revenueManager ? Validators.required : false],
      active: [{ value: insurerEdit.active, disabled: false }, Validators.required]
    });

    this.editForm.reset(insurerEdit);
    this.insurerEdit = insurerEdit;
    this.editFormActive = insurerEdit !== undefined;
  }
}
